import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

const statusColors = {
  unused: "bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300",
  used: "bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-300",
  expired: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
  cancelled: "bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300",
  rejected: "bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300",
  pending: "bg-yellow-100 dark:bg-yellow-950/50 text-yellow-700 dark:text-yellow-300",
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOffer, setFilterOffer] = useState("all");
  const [noteModal, setNoteModal] = useState(null);
  const [noteText, setNoteText] = useState("");
  const { lang } = useApp();
  const tr = t[lang];
  const locale = lang === "ar" ? "ar-SA" : "en-US";

  const statusLabels = {
    unused: tr.unused,
    used: tr.used,
    expired: tr.expired,
    cancelled: tr.cancelled,
    rejected: tr.rejected,
    pending: tr.pending,
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [c, o] = await Promise.all([
      base44.entities.Coupon.list("-created_date", 500),
      base44.entities.Offer.list()
    ]);
    setCoupons(c);
    setOffers(o);
    setLoading(false);
  };

  const updateStatus = async (coupon, status) => {
    await base44.entities.Coupon.update(coupon.id, {
      status,
      ...(status === "used" ? { used_at: new Date().toISOString() } : {})
    });
    loadData();
  };

  const saveNote = async () => {
    await base44.entities.Coupon.update(noteModal.id, { admin_note: noteText });
    setNoteModal(null);
    loadData();
  };

  const filtered = coupons.filter(c => {
    if (searchPhone && !c.customer_phone?.includes(searchPhone)) return false;
    if (searchCode && !c.code?.toUpperCase().includes(searchCode.toUpperCase())) return false;
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    if (filterOffer !== "all" && c.offer_id !== filterOffer) return false;
    return true;
  });

  const thClass = `px-4 py-3 font-bold text-foreground ${lang === "ar" ? "text-right" : "text-left"}`;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-foreground">{tr.couponManagement}</h1>
              <p className="text-muted-foreground text-sm">{filtered.length} {lang === "ar" ? "كوبون" : "coupons"}</p>
            </div>
            <button onClick={loadData} className="px-4 py-2 bg-secondary text-foreground rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors">
              🔄 {tr.refresh}
            </button>
          </div>

          {/* Filters */}
          <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <input
                type="tel"
                placeholder={`🔍 ${tr.searchByPhone}`}
                value={searchPhone}
                onChange={e => setSearchPhone(e.target.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary bg-background text-foreground"
              />
              <input
                type="text"
                placeholder={`🔍 ${tr.searchByCode}`}
                value={searchCode}
                onChange={e => setSearchCode(e.target.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary bg-background text-foreground"
              />
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary bg-background text-foreground"
              >
                <option value="all">{tr.allStatuses}</option>
                {Object.entries(statusLabels).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <select
                value={filterOffer}
                onChange={e => setFilterOffer(e.target.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary bg-background text-foreground"
              >
                <option value="all">{tr.allOffers}</option>
                {offers.map(o => <option key={o.id} value={o.id}>{(lang === "en" && o.title_en) ? o.title_en : o.title}</option>)}
              </select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary/50 text-sm border-b border-border">
                      <th className={thClass}>{tr.code}</th>
                      <th className={thClass}>{tr.offer}</th>
                      <th className={thClass}>{tr.phone}</th>
                      <th className={thClass}>{tr.status}</th>
                      <th className={thClass}>{tr.issued}</th>
                      <th className={thClass}>{tr.expires}</th>
                      <th className={thClass}>{tr.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map(coupon => (
                      <tr key={coupon.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3">
                          <code className="text-sm font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                            {coupon.code}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground max-w-[120px] truncate">
                          {coupon.offer_title || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-foreground" dir="ltr">
                          {coupon.customer_phone}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[coupon.status] || "bg-gray-100 text-gray-600"}`}>
                            {statusLabels[coupon.status] || coupon.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {coupon.issued_at ? new Date(coupon.issued_at).toLocaleDateString(locale) : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString(locale) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {coupon.status === "unused" && (
                              <button
                                onClick={() => updateStatus(coupon, "used")}
                                className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 transition-colors"
                              >
                                ✅ {tr.markUsed}
                              </button>
                            )}
                            {coupon.status !== "cancelled" && (
                              <button
                                onClick={() => updateStatus(coupon, "cancelled")}
                                className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 transition-colors"
                              >
                                ❌ {tr.cancelCoupon}
                              </button>
                            )}
                            <button
                              onClick={() => { setNoteModal(coupon); setNoteText(coupon.admin_note || ""); }}
                              className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                            >
                              📝
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                          <div className="text-4xl mb-2">🎫</div>
                          <p>{tr.noCoupons}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Note Modal */}
        {noteModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="bg-card rounded-2xl p-6 w-full max-w-md shadow-2xl border border-border">
              <h3 className="font-bold text-lg mb-3 text-foreground">📝 {tr.adminNote} - {noteModal.code}</h3>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={4}
                placeholder={tr.noteHere}
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none bg-background text-foreground"
              />
              <div className="flex gap-3 mt-4">
                <button onClick={() => setNoteModal(null)} className="flex-1 py-2.5 border-2 border-border rounded-xl font-medium hover:bg-secondary transition-colors text-foreground">{tr.cancel}</button>
                <button onClick={saveNote} className="flex-1 py-2.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors">{tr.save}</button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AdminGuard>
  );
}